import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import AdminLayout from '../../components/AdminLayout';
import { toast } from 'sonner';

// Lucide icons
import {
  Plus, Trash2, Save, Eye, EyeOff, ChevronUp, ChevronDown,
  GripVertical, Settings, X, Copy, Globe, FileText,
  Type, AlignLeft, Image, Layout, Zap, List, Minus,
  Columns, Code, Video, Grid, ChevronRight, Megaphone,
  Star, Flame, Tag, Mail, Layers, Package, PanelRight,
  ExternalLink, RefreshCw, Check,
  MousePointerClick, AlertTriangle, Tags, AppWindow,
  GalleryHorizontal, Table2, BarChart3, Quote,
  Truck, Trophy, Gift, Sparkles, History
} from 'lucide-react';
import { withDefaults } from '../../lib/homeSections';
import ImageManager from '../../components/admin/ImageManager';

// Shadcn components
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Slider } from '../../components/ui/slider';
import { Separator } from '../../components/ui/separator';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || '';

// ── Block Definitions ────────────────────────────────────────────
const BLOCK_TYPES = [
  { type: 'heading',    icon: Type,       label: 'Heading',       desc: 'H1–H4 headline with alignment & color',       group: 'text' },
  { type: 'paragraph',  icon: AlignLeft,  label: 'Paragraph',     desc: 'Body text with alignment options',            group: 'text' },
  { type: 'image',      icon: Image,      label: 'Image',         desc: 'Full or contained image with caption',        group: 'media' },
  { type: 'image_text', icon: Layout,     label: 'Image + Text',  desc: 'Side-by-side image and text content',         group: 'media' },
  { type: 'video',      icon: Video,      label: 'Video Embed',   desc: 'YouTube or Vimeo embed with caption',         group: 'media' },
  { type: 'cta',        icon: Zap,        label: 'CTA Banner',    desc: 'Title, subtitle, and call-to-action button', group: 'content' },
  { type: 'cards',      icon: Grid,       label: 'Cards Grid',    desc: 'Grid of icon + title + description cards',   group: 'content' },
  { type: 'accordion',  icon: List,       label: 'Accordion/FAQ', desc: 'Expandable Q&A or content sections',         group: 'content' },
  { type: 'columns',    icon: Columns,    label: 'Columns',       desc: '2 or 3 column text layout',                  group: 'layout' },
  { type: 'divider',    icon: Minus,      label: 'Divider',       desc: 'Horizontal separator with optional label',   group: 'layout' },
  { type: 'spacer',     icon: ChevronUp,  label: 'Spacer',        desc: 'Adjustable empty vertical space',            group: 'layout' },
  { type: 'button',     icon: MousePointerClick, label: 'Button', desc: 'Standalone call-to-action button',           group: 'content' },
  { type: 'badges',     icon: Tags,       label: 'Badges',        desc: 'Row of pill labels or tags',                 group: 'content' },
  { type: 'alert',      icon: AlertTriangle, label: 'Alert',      desc: 'Highlighted info, success, or warning box',  group: 'content' },
  { type: 'tabs',       icon: AppWindow,  label: 'Tabs',          desc: 'Tabbed content panels',                      group: 'content' },
  { type: 'carousel',   icon: GalleryHorizontal, label: 'Carousel', desc: 'Swipeable image slideshow',               group: 'media' },
  { type: 'table',      icon: Table2,     label: 'Table',         desc: 'Rows and columns of data',                   group: 'content' },
  { type: 'progress',   icon: BarChart3,  label: 'Progress Bars', desc: 'Labeled progress / stat bars',               group: 'content' },
  { type: 'testimonial',icon: Quote,      label: 'Testimonial',   desc: 'Customer quote with avatar',                 group: 'content' },
  { type: 'html',       icon: Code,       label: 'Custom HTML',   desc: 'Embed raw HTML or script snippets',          group: 'advanced' },
];

const BLOCK_DEFAULTS = {
  heading:    { text: 'Your Headline Here', level: 'h2', align: 'left', color: '#ffffff' },
  paragraph:  { content: 'Add your paragraph text here...', align: 'left' },
  image:      { src: '', alt: '', caption: '', size: 'contained' },
  image_text: { image_src: '', image_alt: '', image_side: 'left', content: 'Describe your product or story here.', title: '' },
  video:      { url: '', caption: '', autoplay: false },
  cta:        { title: 'Ready to Get Started?', subtitle: 'Join thousands of happy customers today.', button_text: 'Shop Now', button_link: '/', bg_color: '#00E5FF', text_color: '#000000', align: 'center' },
  cards:      { title: 'Features', subtitle: '', columns: 3, cards: [{ title: 'Quality', description: 'Premium materials and craftsmanship.', icon: 'star', link: '' }, { title: 'Fast Shipping', description: 'Delivered to your door quickly.', icon: 'truck', link: '' }, { title: 'Satisfaction', description: '100% satisfaction guaranteed.', icon: 'check', link: '' }] },
  accordion:  { title: 'Frequently Asked Questions', items: [{ question: 'What is your return policy?', answer: 'We offer a 30-day hassle-free return policy.' }] },
  columns:    { columns_count: 2, columns: [{ content: 'First column content goes here.' }, { content: 'Second column content goes here.' }] },
  divider:    { style: 'solid', label: '', margin: 'md' },
  spacer:     { size: 'md' },
  button:     { text: 'Click Me', link: '/', variant: 'primary', size: 'md', align: 'left', full_width: false },
  badges:     { align: 'left', variant: 'cyan', items: [{ label: 'New' }, { label: 'Popular' }, { label: 'Limited' }] },
  alert:      { variant: 'info', title: 'Heads up!', content: 'This is an important message for your visitors.' },
  tabs:       { items: [{ label: 'Tab One', content: 'Content for the first tab.' }, { label: 'Tab Two', content: 'Content for the second tab.' }] },
  carousel:   { images: [{ src: '', alt: '', caption: '' }], show_arrows: true },
  table:      { columns: ['Feature', 'Details'], rows: [{ cells: ['Material', '100% cotton'] }, { cells: ['Shipping', 'Free over $50'] }], striped: true },
  progress:   { title: 'Our Progress', items: [{ label: 'Quality', value: 90 }, { label: 'Speed', value: 75 }] },
  testimonial:{ quote: 'This is hands down the best purchase I have made all year!', author: 'Jane Doe', role: 'Verified Buyer', avatar_src: '' },
  html:       { content: '<p>Custom HTML content</p>' },
};

const BLOCK_GROUPS = {
  text: { label: 'Text', color: 'text-blue-600' },
  media: { label: 'Media', color: 'text-purple-600' },
  content: { label: 'Content', color: 'text-green-600' },
  layout: { label: 'Layout', color: 'text-orange-600' },
  advanced: { label: 'Advanced', color: 'text-red-600' },
};

// ── Homepage section meta ────────────────────────────────────────
const HP_SECTIONS = {
  announcement_bar: { label: 'Announcement Bar', icon: Megaphone,  desc: 'Top banner with message & color' },
  hero:             { label: 'Hero Banner',       icon: Layout,     desc: 'Big headline banner plus live sale & new-arrival tiles' },
  trust_bar:        { label: 'Trust Bar',         icon: Truck,      desc: 'Four reasons to buy (shipping, security…)' },
  promotions:       { label: 'Promotions',        icon: Megaphone,  desc: 'Your "Home page" promotion banners' },
  category_grid:    { label: 'Shop by Category',  icon: Tag,        desc: 'Round category tiles' },
  deals_section:    { label: "Today's Deals",     icon: Flame,      desc: 'Items on sale now, with a countdown (hidden when nothing is on sale)' },
  featured_section: { label: 'Featured Picks',    icon: Star,       desc: 'Your Featured items, in your order' },
  best_sellers:     { label: 'Best Sellers',      icon: Trophy,     desc: 'Top sellers from the last 90 days (shows once there are sales)' },
  bundles_section:  { label: 'Bundles & Deals',   icon: Gift,       desc: 'Bundle listings and buy-together deals' },
  new_arrivals:     { label: 'New Arrivals',      icon: Sparkles,   desc: 'Newest products' },
  countdown_banner: { label: 'Countdown Banner',  icon: Flame,      desc: 'Big timer to a date you choose' },
  collections:      { label: 'Collections',       icon: Layers,     desc: 'Grouped product collections' },
  top_rated:        { label: 'Top Rated',         icon: Star,       desc: 'Products rated 4★ and up (shows once reviewed)' },
  recently_viewed:  { label: 'Recently Viewed',   icon: History,    desc: 'Items this shopper looked at (only they see it)' },
  products_section: { label: 'All Products',      icon: Package,    desc: 'Full catalog with search, sorting & category filters' },
  newsletter:       { label: 'Newsletter',        icon: Mail,       desc: 'Email capture with incentive offer' },
};

// ── Helpers ──────────────────────────────────────────────────────
const uid = () => `blk_${Math.random().toString(36).slice(2, 9)}`;
const slugify = s => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

// ── Form helpers using Shadcn ─────────────────────────────────────
function Field({ label, value, onChange, placeholder, hint, type = 'text' }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-ink/70 text-xs uppercase tracking-wider">{label}</Label>
      {hint && <p className="text-ink/45 text-xs">{hint}</p>}
      <Input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="bg-ink/[0.04] border-ink/10 text-ink focus:border-honey placeholder:text-ink/30" />
    </div>
  );
}

function FieldArea({ label, value, onChange, placeholder, hint, rows = 3 }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-ink/70 text-xs uppercase tracking-wider">{label}</Label>
      {hint && <p className="text-ink/45 text-xs">{hint}</p>}
      <Textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows}
        className="bg-ink/[0.04] border-ink/10 text-ink focus:border-honey placeholder:text-ink/30 resize-none" />
    </div>
  );
}

function FieldSelect({ label, value, onChange, options, hint }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-ink/70 text-xs uppercase tracking-wider">{label}</Label>
      {hint && <p className="text-ink/45 text-xs">{hint}</p>}
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="bg-ink/[0.04] border-ink/10 text-ink">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-white border-ink/10">
          {options.map(o => (
            <SelectItem key={o.value} value={o.value} className="text-ink focus:bg-ink/[0.07]">{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function FieldSwitch({ label, value, onChange, hint }) {
  return (
    <div className="flex items-center justify-between py-1">
      <div>
        <Label className="text-ink/75 text-sm">{label}</Label>
        {hint && <p className="text-ink/45 text-xs">{hint}</p>}
      </div>
      <Switch checked={!!value} onCheckedChange={onChange} />
    </div>
  );
}

function FieldColor({ label, value, onChange }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-ink/70 text-xs uppercase tracking-wider">{label}</Label>
      <div className="flex items-center gap-3">
        <input type="color" value={value || '#ffffff'} onChange={e => onChange(e.target.value)}
          className="w-10 h-10 rounded-xl cursor-pointer bg-transparent border border-ink/10 p-1" />
        <Input value={value || ''} onChange={e => onChange(e.target.value)} placeholder="#ffffff" maxLength={7}
          className="bg-ink/[0.04] border-ink/10 text-ink font-mono w-28 focus:border-honey" />
      </div>
    </div>
  );
}

function FieldSlider({ label, value, onChange, min = 0, max = 100, step = 1, unit = '' }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-ink/70 text-xs uppercase tracking-wider">{label}</Label>
        <span className="text-honey-700 text-xs font-mono">{value}{unit}</span>
      </div>
      <Slider value={[value]} onValueChange={([v]) => onChange(v)} min={min} max={max} step={step}
        className="[&>span:first-child]:bg-ink/[0.07] [&>span:first-child>span]:bg-honey [&>span:last-child]:border-honey [&>span:last-child]:bg-honey" />
    </div>
  );
}

function FieldRadio({ label, value, onChange, options }) {
  return (
    <div className="space-y-2">
      <Label className="text-ink/70 text-xs uppercase tracking-wider">{label}</Label>
      <RadioGroup value={value} onValueChange={onChange} className="flex flex-wrap gap-2">
        {options.map(o => (
          <div key={o.value} className="flex items-center">
            <RadioGroupItem value={o.value} id={`rg-${o.value}`} className="sr-only" />
            <label htmlFor={`rg-${o.value}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer border transition-colors ${value === o.value ? 'bg-honey/10 border-honey text-honey-700' : 'bg-ink/[0.04] border-ink/10 text-ink/60 hover:text-ink'}`}>
              {o.label}
            </label>
          </div>
        ))}
      </RadioGroup>
    </div>
  );
}

// ── Block-specific editors ──────────────────────────────────────
function EditorHeading({ data, onChange }) {
  const s = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div className="space-y-4">
      <FieldArea label="Text" value={data.text} onChange={v => s('text', v)} placeholder="Your headline here" rows={2} />
      <FieldSelect label="Level" value={data.level || 'h2'} onChange={v => s('level', v)}
        options={[{ value: 'h1', label: 'H1 — Extra Large' }, { value: 'h2', label: 'H2 — Large' }, { value: 'h3', label: 'H3 — Medium' }, { value: 'h4', label: 'H4 — Small' }]} />
      <FieldRadio label="Alignment" value={data.align || 'left'} onChange={v => s('align', v)}
        options={[{ value: 'left', label: 'Left' }, { value: 'center', label: 'Center' }, { value: 'right', label: 'Right' }]} />
      <FieldColor label="Color" value={data.color || '#ffffff'} onChange={v => s('color', v)} />
    </div>
  );
}

function EditorParagraph({ data, onChange }) {
  const s = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div className="space-y-4">
      <FieldArea label="Content" value={data.content} onChange={v => s('content', v)} placeholder="Paragraph text..." rows={5} />
      <FieldRadio label="Alignment" value={data.align || 'left'} onChange={v => s('align', v)}
        options={[{ value: 'left', label: 'Left' }, { value: 'center', label: 'Center' }, { value: 'right', label: 'Right' }]} />
    </div>
  );
}

function EditorImage({ data, onChange }) {
  const s = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div className="space-y-4">
      <Field label="Image URL" value={data.src} onChange={v => s('src', v)} placeholder="https://..." />
      {data.src && <img src={data.src} alt="" className="w-full h-40 object-cover rounded-xl border border-ink/10" />}
      <Field label="Alt Text" value={data.alt} onChange={v => s('alt', v)} placeholder="Image description" hint="Important for accessibility & SEO" />
      <Field label="Caption (optional)" value={data.caption} onChange={v => s('caption', v)} placeholder="Image caption..." />
      <FieldRadio label="Size" value={data.size || 'contained'} onChange={v => s('size', v)}
        options={[{ value: 'full', label: 'Full Width' }, { value: 'contained', label: 'Contained' }, { value: 'small', label: 'Small' }]} />
    </div>
  );
}

function EditorImageText({ data, onChange }) {
  const s = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div className="space-y-4">
      <Tabs defaultValue="content">
        <TabsList className="bg-ink/[0.04] border border-ink/10 w-full">
          <TabsTrigger value="content" className="data-[state=active]:bg-honey/10 data-[state=active]:text-honey-700 text-ink/60 flex-1">Content</TabsTrigger>
          <TabsTrigger value="image" className="data-[state=active]:bg-honey/10 data-[state=active]:text-honey-700 text-ink/60 flex-1">Image</TabsTrigger>
          <TabsTrigger value="layout" className="data-[state=active]:bg-honey/10 data-[state=active]:text-honey-700 text-ink/60 flex-1">Layout</TabsTrigger>
        </TabsList>
        <TabsContent value="content" className="space-y-4 mt-3">
          <Field label="Title" value={data.title} onChange={v => s('title', v)} placeholder="Section headline" />
          <FieldArea label="Body Text" value={data.content} onChange={v => s('content', v)} placeholder="Describe your content..." rows={5} />
        </TabsContent>
        <TabsContent value="image" className="space-y-4 mt-3">
          <Field label="Image URL" value={data.image_src} onChange={v => s('image_src', v)} placeholder="https://..." />
          {data.image_src && <img src={data.image_src} alt="" className="w-full h-32 object-cover rounded-xl border border-ink/10" />}
          <Field label="Alt Text" value={data.image_alt} onChange={v => s('image_alt', v)} placeholder="Image description" />
        </TabsContent>
        <TabsContent value="layout" className="mt-3">
          <FieldRadio label="Image Position" value={data.image_side || 'left'} onChange={v => s('image_side', v)}
            options={[{ value: 'left', label: 'Image Left' }, { value: 'right', label: 'Image Right' }]} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function EditorVideo({ data, onChange }) {
  const s = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div className="space-y-4">
      <Field label="Video URL" value={data.url} onChange={v => s('url', v)} placeholder="https://youtube.com/watch?v=..." hint="YouTube or Vimeo URL" />
      <Field label="Caption (optional)" value={data.caption} onChange={v => s('caption', v)} placeholder="Video caption..." />
      <FieldSwitch label="Autoplay" value={data.autoplay} onChange={v => s('autoplay', v)} />
    </div>
  );
}

function EditorCTA({ data, onChange }) {
  const s = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div className="space-y-4">
      <Tabs defaultValue="content">
        <TabsList className="bg-ink/[0.04] border border-ink/10 w-full">
          <TabsTrigger value="content" className="data-[state=active]:bg-honey/10 data-[state=active]:text-honey-700 text-ink/60 flex-1">Content</TabsTrigger>
          <TabsTrigger value="style" className="data-[state=active]:bg-honey/10 data-[state=active]:text-honey-700 text-ink/60 flex-1">Style</TabsTrigger>
        </TabsList>
        <TabsContent value="content" className="space-y-4 mt-3">
          <Field label="Title" value={data.title} onChange={v => s('title', v)} placeholder="Ready to get started?" />
          <Field label="Subtitle" value={data.subtitle} onChange={v => s('subtitle', v)} placeholder="Short supporting text..." />
          <Field label="Button Text" value={data.button_text} onChange={v => s('button_text', v)} placeholder="Shop Now" />
          <Field label="Button Link" value={data.button_link} onChange={v => s('button_link', v)} placeholder="/ or https://..." />
        </TabsContent>
        <TabsContent value="style" className="space-y-4 mt-3">
          <FieldColor label="Background Color" value={data.bg_color} onChange={v => s('bg_color', v)} />
          <FieldColor label="Text Color" value={data.text_color} onChange={v => s('text_color', v)} />
          <FieldRadio label="Alignment" value={data.align || 'center'} onChange={v => s('align', v)}
            options={[{ value: 'left', label: 'Left' }, { value: 'center', label: 'Center' }, { value: 'right', label: 'Right' }]} />
          {data.title && (
            <div className="rounded-xl py-6 px-6 text-center" style={{ backgroundColor: data.bg_color || '#00E5FF', color: data.text_color || '#000' }}>
              <p className="font-black text-lg">{data.title}</p>
              {data.subtitle && <p className="text-sm mt-1 opacity-80">{data.subtitle}</p>}
              {data.button_text && <span className="inline-block mt-3 px-5 py-2 bg-black/20 rounded-full text-sm font-bold">{data.button_text}</span>}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function EditorCards({ data, onChange }) {
  const s = (k, v) => onChange({ ...data, [k]: v });
  const updateCard = (i, k, v) => {
    const cards = [...(data.cards || [])];
    cards[i] = { ...cards[i], [k]: v };
    s('cards', cards);
  };
  const addCard = () => s('cards', [...(data.cards || []), { title: 'New Card', description: '', icon: 'star', link: '' }]);
  const removeCard = (i) => s('cards', data.cards.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-4">
      <Field label="Section Title" value={data.title} onChange={v => s('title', v)} placeholder="Features" />
      <Field label="Subtitle" value={data.subtitle} onChange={v => s('subtitle', v)} placeholder="Optional subtitle" />
      <FieldSelect label="Columns" value={String(data.columns || 3)} onChange={v => s('columns', parseInt(v))}
        options={[{ value: '2', label: '2 Columns' }, { value: '3', label: '3 Columns' }, { value: '4', label: '4 Columns' }]} />
      <Separator className="bg-ink/[0.07]" />
      <div className="space-y-3">
        {(data.cards || []).map((card, i) => (
          <Accordion type="single" collapsible key={i}>
            <AccordionItem value={`card-${i}`} className="border border-ink/10 rounded-xl overflow-hidden">
              <AccordionTrigger className="px-4 py-3 text-ink/75 hover:text-ink text-sm hover:no-underline">
                <span className="font-medium">{card.title || `Card ${i + 1}`}</span>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4 space-y-3">
                <Field label="Title" value={card.title} onChange={v => updateCard(i, 'title', v)} placeholder="Card title" />
                <FieldArea label="Description" value={card.description} onChange={v => updateCard(i, 'description', v)} placeholder="Card description" rows={2} />
                <Field label="Link (optional)" value={card.link} onChange={v => updateCard(i, 'link', v)} placeholder="/" />
                <Button variant="destructive" size="sm" onClick={() => removeCard(i)} className="w-full bg-red-500/10 text-red-600 border border-red-500/20 hover:bg-red-500/20">
                  <Trash2 className="w-3 h-3 mr-2" /> Remove Card
                </Button>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        ))}
      </div>
      <Button onClick={addCard} variant="outline" size="sm" className="w-full border-ink/10 text-ink/70 hover:text-ink hover:border-ink/15 bg-ink/[0.04]">
        <Plus className="w-3 h-3 mr-2" /> Add Card
      </Button>
    </div>
  );
}

function EditorAccordion({ data, onChange }) {
  const s = (k, v) => onChange({ ...data, [k]: v });
  const updateItem = (i, k, v) => {
    const items = [...(data.items || [])];
    items[i] = { ...items[i], [k]: v };
    s('items', items);
  };
  const addItem = () => s('items', [...(data.items || []), { question: 'New Question', answer: 'Answer goes here.' }]);
  const removeItem = (i) => s('items', data.items.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-4">
      <Field label="Section Title" value={data.title} onChange={v => s('title', v)} placeholder="Frequently Asked Questions" />
      <Separator className="bg-ink/[0.07]" />
      <div className="space-y-3">
        {(data.items || []).map((item, i) => (
          <div key={i} className="border border-ink/10 rounded-xl p-3 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-ink/55 text-xs uppercase">Item {i + 1}</Label>
              <Button variant="ghost" size="sm" onClick={() => removeItem(i)} className="h-6 px-2 text-red-600 hover:text-red-600 hover:bg-red-500/10">
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
            <Field label="Question" value={item.question} onChange={v => updateItem(i, 'question', v)} placeholder="Question..." />
            <FieldArea label="Answer" value={item.answer} onChange={v => updateItem(i, 'answer', v)} placeholder="Answer..." rows={3} />
          </div>
        ))}
      </div>
      <Button onClick={addItem} variant="outline" size="sm" className="w-full border-ink/10 text-ink/70 hover:text-ink bg-ink/[0.04]">
        <Plus className="w-3 h-3 mr-2" /> Add Item
      </Button>
    </div>
  );
}

function EditorColumns({ data, onChange }) {
  const s = (k, v) => onChange({ ...data, [k]: v });
  const colCount = data.columns_count || 2;
  const cols = data.columns || Array(colCount).fill({ content: '' });
  const updateCol = (i, v) => {
    const next = [...cols];
    next[i] = { ...next[i], content: v };
    s('columns', next);
  };
  return (
    <div className="space-y-4">
      <FieldSelect label="Columns" value={String(colCount)} onChange={v => { const n = parseInt(v); s('columns_count', n); s('columns', Array(n).fill({ content: '' }).map((_, i) => cols[i] || { content: '' })); }}
        options={[{ value: '2', label: '2 Columns' }, { value: '3', label: '3 Columns' }]} />
      {Array.from({ length: colCount }, (_, i) => (
        <FieldArea key={i} label={`Column ${i + 1}`} value={cols[i]?.content || ''} onChange={v => updateCol(i, v)} placeholder="Column content..." rows={4} />
      ))}
    </div>
  );
}

function EditorDivider({ data, onChange }) {
  const s = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div className="space-y-4">
      <FieldSelect label="Style" value={data.style || 'solid'} onChange={v => s('style', v)}
        options={[{ value: 'solid', label: 'Solid Line' }, { value: 'dashed', label: 'Dashed Line' }, { value: 'dotted', label: 'Dotted' }]} />
      <Field label="Label (optional)" value={data.label} onChange={v => s('label', v)} placeholder="e.g. ✦ or section text" hint="Leave blank for a plain line" />
      <FieldSelect label="Margin" value={data.margin || 'md'} onChange={v => s('margin', v)}
        options={[{ value: 'sm', label: 'Small' }, { value: 'md', label: 'Medium' }, { value: 'lg', label: 'Large' }, { value: 'xl', label: 'Extra Large' }]} />
    </div>
  );
}

function EditorSpacer({ data, onChange }) {
  const sizes = { xs: 16, sm: 32, md: 48, lg: 80, xl: 128 };
  const s = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div className="space-y-4">
      <FieldSelect label="Height" value={data.size || 'md'} onChange={v => s('size', v)}
        options={Object.entries(sizes).map(([k, px]) => ({ value: k, label: `${k.toUpperCase()} — ${px}px` }))} />
    </div>
  );
}

function EditorHTML({ data, onChange }) {
  const s = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div className="space-y-4">
      <Alert className="border-yellow-500/20 bg-yellow-500/5">
        <AlertDescription className="text-amber-600 text-xs">
          Raw HTML is rendered as-is. Use for embeds, custom styles, or scripts. Be careful with untrusted content.
        </AlertDescription>
      </Alert>
      <FieldArea label="HTML Content" value={data.content} onChange={v => s('content', v)} placeholder="<p>Custom HTML...</p>" rows={10} />
    </div>
  );
}

function EditorButton({ data, onChange }) {
  const s = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div className="space-y-4">
      <Field label="Button Text" value={data.text} onChange={v => s('text', v)} placeholder="Click Me" />
      <Field label="Link" value={data.link} onChange={v => s('link', v)} placeholder="/ or https://..." />
      <FieldSelect label="Style" value={data.variant || 'primary'} onChange={v => s('variant', v)}
        options={[{ value: 'primary', label: 'Primary (Aqua)' }, { value: 'secondary', label: 'Secondary' }, { value: 'outline', label: 'Outline' }, { value: 'ghost', label: 'Ghost' }]} />
      <FieldSelect label="Size" value={data.size || 'md'} onChange={v => s('size', v)}
        options={[{ value: 'sm', label: 'Small' }, { value: 'md', label: 'Medium' }, { value: 'lg', label: 'Large' }]} />
      <FieldRadio label="Alignment" value={data.align || 'left'} onChange={v => s('align', v)}
        options={[{ value: 'left', label: 'Left' }, { value: 'center', label: 'Center' }, { value: 'right', label: 'Right' }]} />
      <FieldSwitch label="Full Width" value={data.full_width} onChange={v => s('full_width', v)} />
    </div>
  );
}

function EditorBadges({ data, onChange }) {
  const s = (k, v) => onChange({ ...data, [k]: v });
  const updateItem = (i, v) => { const items = [...(data.items || [])]; items[i] = { ...items[i], label: v }; s('items', items); };
  const addItem = () => s('items', [...(data.items || []), { label: 'New' }]);
  const removeItem = (i) => s('items', data.items.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-4">
      <FieldSelect label="Color" value={data.variant || 'cyan'} onChange={v => s('variant', v)}
        options={[{ value: 'cyan', label: 'Aqua' }, { value: 'gold', label: 'Gold' }, { value: 'green', label: 'Green' }, { value: 'red', label: 'Red' }, { value: 'white', label: 'White' }]} />
      <FieldRadio label="Alignment" value={data.align || 'left'} onChange={v => s('align', v)}
        options={[{ value: 'left', label: 'Left' }, { value: 'center', label: 'Center' }, { value: 'right', label: 'Right' }]} />
      <Separator className="bg-ink/[0.07]" />
      <div className="space-y-2">
        {(data.items || []).map((item, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input value={item.label || ''} onChange={e => updateItem(i, e.target.value)} placeholder="Badge label"
              className="bg-ink/[0.04] border-ink/10 text-ink focus:border-honey" />
            <Button variant="ghost" size="sm" onClick={() => removeItem(i)} className="h-8 w-8 p-0 text-red-600/60 hover:text-red-600 hover:bg-red-500/10 flex-shrink-0">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        ))}
      </div>
      <Button onClick={addItem} variant="outline" size="sm" className="w-full border-ink/10 text-ink/70 hover:text-ink bg-ink/[0.04]">
        <Plus className="w-3 h-3 mr-2" /> Add Badge
      </Button>
    </div>
  );
}

function EditorAlert({ data, onChange }) {
  const s = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div className="space-y-4">
      <FieldSelect label="Type" value={data.variant || 'info'} onChange={v => s('variant', v)}
        options={[{ value: 'info', label: 'Info (Aqua)' }, { value: 'success', label: 'Success (Green)' }, { value: 'warning', label: 'Warning (Gold)' }, { value: 'error', label: 'Error (Red)' }]} />
      <Field label="Title" value={data.title} onChange={v => s('title', v)} placeholder="Heads up!" />
      <FieldArea label="Message" value={data.content} onChange={v => s('content', v)} placeholder="Your message..." rows={3} />
    </div>
  );
}

function EditorTabs({ data, onChange }) {
  const s = (k, v) => onChange({ ...data, [k]: v });
  const updateItem = (i, k, v) => { const items = [...(data.items || [])]; items[i] = { ...items[i], [k]: v }; s('items', items); };
  const addItem = () => s('items', [...(data.items || []), { label: `Tab ${(data.items?.length || 0) + 1}`, content: '' }]);
  const removeItem = (i) => s('items', data.items.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {(data.items || []).map((item, i) => (
          <Accordion type="single" collapsible key={i}>
            <AccordionItem value={`tab-${i}`} className="border border-ink/10 rounded-xl overflow-hidden">
              <AccordionTrigger className="px-4 py-3 text-ink/75 hover:text-ink text-sm hover:no-underline">
                <span className="font-medium">{item.label || `Tab ${i + 1}`}</span>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4 space-y-3">
                <Field label="Tab Label" value={item.label} onChange={v => updateItem(i, 'label', v)} placeholder="Tab title" />
                <FieldArea label="Tab Content" value={item.content} onChange={v => updateItem(i, 'content', v)} placeholder="Tab content..." rows={4} />
                <Button variant="destructive" size="sm" onClick={() => removeItem(i)} className="w-full bg-red-500/10 text-red-600 border border-red-500/20 hover:bg-red-500/20">
                  <Trash2 className="w-3 h-3 mr-2" /> Remove Tab
                </Button>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        ))}
      </div>
      <Button onClick={addItem} variant="outline" size="sm" className="w-full border-ink/10 text-ink/70 hover:text-ink bg-ink/[0.04]">
        <Plus className="w-3 h-3 mr-2" /> Add Tab
      </Button>
    </div>
  );
}

function EditorCarousel({ data, onChange }) {
  const s = (k, v) => onChange({ ...data, [k]: v });
  const updateImg = (i, k, v) => { const images = [...(data.images || [])]; images[i] = { ...images[i], [k]: v }; s('images', images); };
  const addImg = () => s('images', [...(data.images || []), { src: '', alt: '', caption: '' }]);
  const removeImg = (i) => s('images', data.images.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-4">
      <FieldSwitch label="Show Arrows" value={data.show_arrows !== false} onChange={v => s('show_arrows', v)} />
      <Separator className="bg-ink/[0.07]" />
      <div className="space-y-3">
        {(data.images || []).map((img, i) => (
          <div key={i} className="border border-ink/10 rounded-xl p-3 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-ink/55 text-xs uppercase">Slide {i + 1}</Label>
              <Button variant="ghost" size="sm" onClick={() => removeImg(i)} className="h-6 px-2 text-red-600 hover:text-red-600 hover:bg-red-500/10">
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
            <Field label="Image URL" value={img.src} onChange={v => updateImg(i, 'src', v)} placeholder="https://..." />
            {img.src && <img src={img.src} alt="" className="w-full h-28 object-cover rounded-lg border border-ink/10" />}
            <Field label="Alt Text" value={img.alt} onChange={v => updateImg(i, 'alt', v)} placeholder="Description" />
            <Field label="Caption (optional)" value={img.caption} onChange={v => updateImg(i, 'caption', v)} placeholder="Caption" />
          </div>
        ))}
      </div>
      <Button onClick={addImg} variant="outline" size="sm" className="w-full border-ink/10 text-ink/70 hover:text-ink bg-ink/[0.04]">
        <Plus className="w-3 h-3 mr-2" /> Add Slide
      </Button>
    </div>
  );
}

function EditorTable({ data, onChange }) {
  const s = (k, v) => onChange({ ...data, [k]: v });
  const columns = data.columns || [];
  const rows = data.rows || [];
  const setColumn = (i, v) => { const next = [...columns]; next[i] = v; s('columns', next); };
  const addColumn = () => { s('columns', [...columns, `Column ${columns.length + 1}`]); s('rows', rows.map(r => ({ cells: [...(r.cells || []), ''] }))); };
  const removeColumn = (i) => { s('columns', columns.filter((_, idx) => idx !== i)); s('rows', rows.map(r => ({ cells: (r.cells || []).filter((_, idx) => idx !== i) }))); };
  const setCell = (ri, ci, v) => { const next = rows.map((r, idx) => idx === ri ? { cells: (r.cells || []).map((c, cidx) => cidx === ci ? v : c) } : r); s('rows', next); };
  const addRow = () => s('rows', [...rows, { cells: columns.map(() => '') }]);
  const removeRow = (i) => s('rows', rows.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-4">
      <FieldSwitch label="Striped Rows" value={data.striped !== false} onChange={v => s('striped', v)} />
      <Separator className="bg-ink/[0.07]" />
      <div className="space-y-2">
        <Label className="text-ink/70 text-xs uppercase tracking-wider">Columns</Label>
        {columns.map((col, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input value={col} onChange={e => setColumn(i, e.target.value)} placeholder={`Column ${i + 1}`}
              className="bg-ink/[0.04] border-ink/10 text-ink focus:border-honey" />
            <Button variant="ghost" size="sm" onClick={() => removeColumn(i)} className="h-8 w-8 p-0 text-red-600/60 hover:text-red-600 hover:bg-red-500/10 flex-shrink-0">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        ))}
        <Button onClick={addColumn} variant="outline" size="sm" className="w-full border-ink/10 text-ink/70 hover:text-ink bg-ink/[0.04]">
          <Plus className="w-3 h-3 mr-2" /> Add Column
        </Button>
      </div>
      <Separator className="bg-ink/[0.07]" />
      <div className="space-y-3">
        <Label className="text-ink/70 text-xs uppercase tracking-wider">Rows</Label>
        {rows.map((row, ri) => (
          <div key={ri} className="border border-ink/10 rounded-xl p-3 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-ink/55 text-xs uppercase">Row {ri + 1}</Label>
              <Button variant="ghost" size="sm" onClick={() => removeRow(ri)} className="h-6 px-2 text-red-600 hover:text-red-600 hover:bg-red-500/10">
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
            {columns.map((col, ci) => (
              <Field key={ci} label={col || `Column ${ci + 1}`} value={row.cells?.[ci]} onChange={v => setCell(ri, ci, v)} placeholder="Cell value" />
            ))}
          </div>
        ))}
        <Button onClick={addRow} variant="outline" size="sm" className="w-full border-ink/10 text-ink/70 hover:text-ink bg-ink/[0.04]">
          <Plus className="w-3 h-3 mr-2" /> Add Row
        </Button>
      </div>
    </div>
  );
}

function EditorProgress({ data, onChange }) {
  const s = (k, v) => onChange({ ...data, [k]: v });
  const updateItem = (i, k, v) => { const items = [...(data.items || [])]; items[i] = { ...items[i], [k]: v }; s('items', items); };
  const addItem = () => s('items', [...(data.items || []), { label: 'New Metric', value: 50 }]);
  const removeItem = (i) => s('items', data.items.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-4">
      <Field label="Section Title (optional)" value={data.title} onChange={v => s('title', v)} placeholder="Our Progress" />
      <Separator className="bg-ink/[0.07]" />
      <div className="space-y-3">
        {(data.items || []).map((item, i) => (
          <div key={i} className="border border-ink/10 rounded-xl p-3 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-ink/55 text-xs uppercase">Bar {i + 1}</Label>
              <Button variant="ghost" size="sm" onClick={() => removeItem(i)} className="h-6 px-2 text-red-600 hover:text-red-600 hover:bg-red-500/10">
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
            <Field label="Label" value={item.label} onChange={v => updateItem(i, 'label', v)} placeholder="Quality" />
            <FieldSlider label="Value" value={item.value ?? 50} onChange={v => updateItem(i, 'value', v)} min={0} max={100} unit="%" />
          </div>
        ))}
      </div>
      <Button onClick={addItem} variant="outline" size="sm" className="w-full border-ink/10 text-ink/70 hover:text-ink bg-ink/[0.04]">
        <Plus className="w-3 h-3 mr-2" /> Add Bar
      </Button>
    </div>
  );
}

function EditorTestimonial({ data, onChange }) {
  const s = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div className="space-y-4">
      <FieldArea label="Quote" value={data.quote} onChange={v => s('quote', v)} placeholder="This product changed my life..." rows={4} />
      <Field label="Author Name" value={data.author} onChange={v => s('author', v)} placeholder="Jane Doe" />
      <Field label="Role / Subtitle" value={data.role} onChange={v => s('role', v)} placeholder="Verified Buyer" />
      <Field label="Avatar Image URL (optional)" value={data.avatar_src} onChange={v => s('avatar_src', v)} placeholder="https://..." />
      {data.avatar_src && <img src={data.avatar_src} alt="" className="w-16 h-16 rounded-full object-cover border border-ink/10" />}
    </div>
  );
}

// ── Homepage section editors (Shadcn-upgraded) ────────────────────
function EditorHPAnnouncement({ cfg, onChange }) {
  const s = (k, v) => onChange({ ...cfg, [k]: v });
  return (
    <div className="space-y-4">
      <FieldArea label="Message" value={cfg.text} onChange={v => s('text', v)} placeholder="Free shipping on orders over $50!" rows={2} />
      <Field label="Link URL (optional)" value={cfg.link} onChange={v => s('link', v)} placeholder="https://..." />
      <div className="grid grid-cols-2 gap-4">
        <FieldColor label="Background" value={cfg.bg_color || '#00E5FF'} onChange={v => s('bg_color', v)} />
        <FieldColor label="Text Color" value={cfg.text_color || '#000000'} onChange={v => s('text_color', v)} />
      </div>
      {cfg.text && (
        <div className="rounded-xl py-3 px-4 text-center text-sm font-bold"
          style={{ backgroundColor: cfg.bg_color || '#00E5FF', color: cfg.text_color || '#000' }}>
          {cfg.text}
        </div>
      )}
    </div>
  );
}

function EditorHPHero({ cfg, onChange }) {
  const s = (k, v) => onChange({ ...cfg, [k]: v });
  return (
    <div className="space-y-4">
      <Field label="Badge Text" value={cfg.badge_text} onChange={v => s('badge_text', v)} placeholder="e.g. New arrivals weekly" />
      <Field label="Title Line 1" value={cfg.title_line1} onChange={v => s('title_line1', v)} placeholder="Main headline" />
      <Field label="Title Line 2 (highlighted)" value={cfg.title_line2} onChange={v => s('title_line2', v)} placeholder="Highlighted sub-headline" hint="Shown with a honey highlighter underline" />
      <FieldArea label="Description" value={cfg.description} onChange={v => s('description', v)} placeholder="Short description" rows={3} />
      <Field label="CTA Button Text" value={cfg.cta_text} onChange={v => s('cta_text', v)} placeholder="Shop Now" />
      <Field label="CTA Link" value={cfg.cta_link} onChange={v => s('cta_link', v)} placeholder="#products" />
      <div className="space-y-1.5">
        <Label className="text-ink/70 text-xs uppercase tracking-wider">Banner image (optional)</Label>
        <p className="text-ink/45 text-xs">Shown on the right side of the banner, fading into the background. A lifestyle photo works best.</p>
        <ImageManager images={cfg.image ? [cfg.image] : []} onChange={imgs => s('image', imgs[imgs.length - 1] || '')} multiple={false} subfolder="banners" />
      </div>
    </div>
  );
}

function EditorHPCountdown({ cfg, onChange }) {
  const s = (k, v) => onChange({ ...cfg, [k]: v });
  const toLocal = iso => iso ? iso.slice(0, 16) : '';
  return (
    <div className="space-y-4">
      <Alert className="border-honey/20 bg-honey/5">
        <AlertDescription className="text-honey-600 text-xs">Tip: Create a Sale in Admin → Sales first, then set end date here to match.</AlertDescription>
      </Alert>
      <Field label="Sale Title" value={cfg.title} onChange={v => s('title', v)} placeholder="Flash Sale!" />
      <Field label="Subtitle" value={cfg.subtitle} onChange={v => s('subtitle', v)} placeholder="Prices cut up to 50% off" />
      <div className="space-y-1.5">
        <Label className="text-ink/70 text-xs uppercase tracking-wider">Sale End Date & Time *</Label>
        <input type="datetime-local" value={toLocal(cfg.end_date)}
          onChange={e => s('end_date', e.target.value ? e.target.value + ':00' : '')}
          className="w-full bg-ink/[0.04] border border-ink/10 rounded-lg px-4 py-2.5 text-ink text-sm focus:outline-none focus:border-honey" />
        {cfg.end_date && new Date(cfg.end_date) < new Date() && (
          <p className="text-red-600 text-xs mt-1">This date has passed — banner will be hidden.</p>
        )}
      </div>
      <Field label="CTA Button Text" value={cfg.cta_text} onChange={v => s('cta_text', v)} placeholder="Shop Now" />
      <Field label="CTA Link" value={cfg.cta_link} onChange={v => s('cta_link', v)} placeholder="#products" />
    </div>
  );
}

function EditorHPFeatured({ cfg, onChange }) {
  const s = (k, v) => onChange({ ...cfg, [k]: v });
  return (
    <div className="space-y-4">
      <Field label="Section Title" value={cfg.title} onChange={v => s('title', v)} placeholder="Featured Products" />
      <Field label="Subtitle" value={cfg.subtitle} onChange={v => s('subtitle', v)} placeholder="Handpicked favorites" />
      <div className="space-y-1.5">
        <Label className="text-ink/70 text-xs uppercase tracking-wider">Max Items (1–12)</Label>
        <Input type="number" min={1} max={12} value={cfg.max_items || 6} onChange={e => s('max_items', parseInt(e.target.value))}
          className="w-24 bg-ink/[0.04] border-ink/10 text-ink focus:border-honey" />
      </div>
      <FieldSwitch label='Show "View All" link' value={cfg.show_view_all} onChange={v => s('show_view_all', v)} />
    </div>
  );
}

function EditorHPGeneric({ cfg, onChange, fields }) {
  const s = (k, v) => onChange({ ...cfg, [k]: v });
  return (
    <div className="space-y-4">
      {fields.map(f => <Field key={f.key} label={f.label} value={cfg[f.key]} onChange={v => s(f.key, v)} placeholder={f.placeholder} />)}
    </div>
  );
}

const HP_EDITORS = {
  announcement_bar: EditorHPAnnouncement,
  hero:             EditorHPHero,
  countdown_banner: EditorHPCountdown,
  featured_section: EditorHPFeatured,
  category_grid: ({ cfg, onChange }) => <EditorHPGeneric cfg={cfg} onChange={onChange} fields={[{ key: 'title', label: 'Section Title', placeholder: 'Shop by Category' }, { key: 'subtitle', label: 'Subtitle', placeholder: 'Find exactly what you need' }]} />,
  products_section: ({ cfg, onChange }) => {
    const s = (k, v) => onChange({ ...cfg, [k]: v });
    return (
      <div className="space-y-4">
        <Field label="Section Title" value={cfg.title} onChange={v => s('title', v)} placeholder="All Products" />
        <FieldSwitch label="Show search bar" value={cfg.show_search} onChange={v => s('show_search', v)} />
        <FieldSwitch label="Show category filters" value={cfg.show_categories} onChange={v => s('show_categories', v)} />
      </div>
    );
  },
  trust_bar: ({ cfg, onChange }) => <EditorHPGeneric cfg={cfg} onChange={onChange} fields={[
    { key: 'item1', label: 'Item 1 (replaced by "Free shipping over $X" when you have a free-shipping threshold)', placeholder: 'Fast, tracked shipping' },
    { key: 'item2', label: 'Item 2', placeholder: 'Secure checkout' }, { key: 'item3', label: 'Item 3', placeholder: 'Hand-picked quality' },
    { key: 'item4', label: 'Item 4', placeholder: 'Friendly support' }]} />,
  promotions: () => <p className="text-ink/60 text-sm">This spot shows your promotions set to "Home page". Create and schedule them in Admin → Promotions.</p>,
  deals_section: ({ cfg, onChange }) => <EditorHPGeneric cfg={cfg} onChange={onChange} fields={[{ key: 'title', label: 'Section Title', placeholder: "Today's deals" }, { key: 'subtitle', label: 'Subtitle', placeholder: '' }, { key: 'max_items', label: 'Max items', placeholder: '10' }]} />,
  best_sellers: ({ cfg, onChange }) => <EditorHPGeneric cfg={cfg} onChange={onChange} fields={[{ key: 'title', label: 'Section Title', placeholder: 'Best sellers' }, { key: 'subtitle', label: 'Subtitle', placeholder: '' }, { key: 'max_items', label: 'Max items', placeholder: '10' }]} />,
  bundles_section: ({ cfg, onChange }) => <EditorHPGeneric cfg={cfg} onChange={onChange} fields={[{ key: 'title', label: 'Section Title', placeholder: 'Better together' }, { key: 'subtitle', label: 'Subtitle', placeholder: '' }]} />,
  new_arrivals: ({ cfg, onChange }) => <EditorHPGeneric cfg={cfg} onChange={onChange} fields={[{ key: 'title', label: 'Section Title', placeholder: 'Just landed' }, { key: 'subtitle', label: 'Subtitle', placeholder: '' }, { key: 'max_items', label: 'Max items', placeholder: '12' }]} />,
  top_rated: ({ cfg, onChange }) => <EditorHPGeneric cfg={cfg} onChange={onChange} fields={[{ key: 'title', label: 'Section Title', placeholder: 'Top rated' }, { key: 'subtitle', label: 'Subtitle', placeholder: '' }, { key: 'max_items', label: 'Max items', placeholder: '10' }]} />,
  recently_viewed: ({ cfg, onChange }) => <EditorHPGeneric cfg={cfg} onChange={onChange} fields={[{ key: 'title', label: 'Section Title', placeholder: 'Recently viewed' }]} />,
  collections: ({ cfg, onChange }) => <EditorHPGeneric cfg={cfg} onChange={onChange} fields={[{ key: 'title', label: 'Section Title', placeholder: 'Curated Collections' }, { key: 'subtitle', label: 'Subtitle', placeholder: 'Handpicked sets for every style' }]} />,
  newsletter: ({ cfg, onChange }) => {
    const s = (k, v) => onChange({ ...cfg, [k]: v });
    return (
      <div className="space-y-4">
        <Field label="Headline" value={cfg.title} onChange={v => s('title', v)} placeholder="Get 10% Off Your First Order" />
        <Field label="Subtext" value={cfg.subtitle} onChange={v => s('subtitle', v)} placeholder="Subscribe for exclusive deals..." />
        <Field label="Incentive Badge" value={cfg.incentive_badge} onChange={v => s('incentive_badge', v)} placeholder="10% OFF" hint="Short bold text in gold badge above headline" />
        <Field label="Input Placeholder" value={cfg.placeholder} onChange={v => s('placeholder', v)} placeholder="Enter your email..." />
        <Field label="Button Text" value={cfg.button_text} onChange={v => s('button_text', v)} placeholder="Subscribe" />
      </div>
    );
  },
};

const BLOCK_EDITORS = {
  heading: EditorHeading, paragraph: EditorParagraph, image: EditorImage,
  image_text: EditorImageText, video: EditorVideo, cta: EditorCTA,
  cards: EditorCards, accordion: EditorAccordion, columns: EditorColumns,
  divider: EditorDivider, spacer: EditorSpacer, html: EditorHTML,
  button: EditorButton, badges: EditorBadges, alert: EditorAlert,
  tabs: EditorTabs, carousel: EditorCarousel, table: EditorTable,
  progress: EditorProgress, testimonial: EditorTestimonial,
};

// ── Block summary text for canvas cards ─────────────────────────
function blockSummary(block) {
  switch (block.type) {
    case 'heading':    return block.data?.text?.slice(0, 60) || '—';
    case 'paragraph':  return block.data?.content?.slice(0, 80) || '—';
    case 'image':      return block.data?.src ? '📷 ' + (block.data.alt || 'Image') : 'No image set';
    case 'image_text': return block.data?.title || 'Image + text block';
    case 'cta':        return `"${block.data?.button_text || 'Button'}" → ${block.data?.button_link || '/'}`;
    case 'cards':      return `${block.data?.cards?.length || 0} cards${block.data?.title ? ` — ${block.data.title}` : ''}`;
    case 'accordion':  return `${block.data?.items?.length || 0} items${block.data?.title ? ` — ${block.data.title}` : ''}`;
    case 'video':      return block.data?.url || 'No URL set';
    case 'columns':    return `${block.data?.columns_count || 2} columns`;
    case 'divider':    return block.data?.label || block.data?.style || 'Horizontal rule';
    case 'spacer':     return `Height: ${block.data?.size || 'md'}`;
    case 'button':     return `"${block.data?.text || 'Button'}" → ${block.data?.link || '/'}`;
    case 'badges':     return `${block.data?.items?.length || 0} badges`;
    case 'alert':      return `${block.data?.variant || 'info'} — ${block.data?.title || 'Alert'}`;
    case 'tabs':       return `${block.data?.items?.length || 0} tabs`;
    case 'carousel':   return `${block.data?.images?.length || 0} slides`;
    case 'table':      return `${block.data?.columns?.length || 0} cols × ${block.data?.rows?.length || 0} rows`;
    case 'progress':   return `${block.data?.items?.length || 0} bars${block.data?.title ? ` — ${block.data.title}` : ''}`;
    case 'testimonial':return block.data?.author || 'Testimonial';
    case 'html':       return 'Custom HTML block';
    default:           return block.type;
  }
}

// ── Add Block Dialog ─────────────────────────────────────────────
function AddBlockDialog({ open, onClose, onAdd }) {
  const [search, setSearch] = useState('');
  const filtered = BLOCK_TYPES.filter(b => !search || b.label.toLowerCase().includes(search.toLowerCase()) || b.desc.toLowerCase().includes(search.toLowerCase()));
  const grouped = Object.entries(BLOCK_GROUPS).map(([key, meta]) => ({
    ...meta, key, blocks: filtered.filter(b => b.group === key)
  })).filter(g => g.blocks.length > 0);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white border-ink/10 max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-ink font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>Add a Block</DialogTitle>
        </DialogHeader>
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search blocks..."
          className="bg-ink/[0.04] border-ink/10 text-ink placeholder:text-ink/45" />
        <ScrollArea className="flex-1 mt-3">
          <div className="space-y-5 pr-2">
            {grouped.map(group => (
              <div key={group.key}>
                <p className={`text-xs font-bold uppercase tracking-widest mb-2 ${group.color}`}>{group.label}</p>
                <div className="grid grid-cols-2 gap-2">
                  {group.blocks.map(bt => {
                    const Icon = bt.icon;
                    return (
                      <button key={bt.type} onClick={() => { onAdd(bt.type); onClose(); }}
                        data-testid={`add-block-${bt.type}`}
                        className="flex items-start gap-3 p-3 rounded-xl bg-ink/[0.04] border border-ink/10 hover:border-honey/40 hover:bg-honey/5 transition-all text-left group">
                        <div className="w-8 h-8 rounded-lg bg-ink/[0.04] flex items-center justify-center flex-shrink-0 group-hover:bg-honey/10">
                          <Icon className="w-4 h-4 text-ink/60 group-hover:text-honey-700" />
                        </div>
                        <div>
                          <p className="text-ink text-sm font-semibold">{bt.label}</p>
                          <p className="text-ink/55 text-xs leading-tight">{bt.desc}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

// ── New Page Dialog ──────────────────────────────────────────────
function NewPageDialog({ open, onClose, onCreate }) {
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [autoSlug, setAutoSlug] = useState(true);
  const [showInNav, setShowInNav] = useState(false);

  const handleTitleChange = (v) => {
    setTitle(v);
    if (autoSlug) setSlug(slugify(v));
  };

  const handleCreate = () => {
    if (!title.trim()) return;
    onCreate({ title, slug: slug || slugify(title), status: 'draft', show_in_nav: showInNav, blocks: [] });
    setTitle(''); setSlug(''); setAutoSlug(true); setShowInNav(false); onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white border-ink/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-ink font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>New Page</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <Field label="Page Title" value={title} onChange={handleTitleChange} placeholder="About Us" />
          <div className="space-y-1.5">
            <Label className="text-ink/70 text-xs uppercase tracking-wider">URL Slug</Label>
            <div className="flex items-center gap-2">
              <span className="text-ink/45 text-sm">/pages/</span>
              <Input value={slug} onChange={e => { setSlug(slugify(e.target.value)); setAutoSlug(false); }} placeholder="about-us"
                className="bg-ink/[0.04] border-ink/10 text-ink font-mono text-sm focus:border-honey" />
            </div>
          </div>
          <div className="flex items-center justify-between py-2 px-3 rounded-xl bg-ink/[0.04] border border-ink/10">
            <div>
              <p className="text-ink/75 text-sm font-medium">Show in Navbar</p>
              <p className="text-ink/45 text-xs">Display a link to this page in the storefront navigation</p>
            </div>
            <Switch checked={showInNav} onCheckedChange={setShowInNav} />
          </div>
          <div className="flex gap-3 pt-2">
            <Button onClick={handleCreate} disabled={!title.trim()}
              className="flex-1 bg-honey text-black font-bold hover:bg-honey-600">Create Page</Button>
            <Button onClick={onClose} variant="outline" className="border-ink/10 text-ink/70 hover:text-ink bg-ink/[0.04]">Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Main PageBuilder ─────────────────────────────────────────────
export default function PageBuilder() {
  // Pages state
  const [pages, setPages] = useState([]);
  const [selectedPageId, setSelectedPageId] = useState('homepage');
  const [customPage, setCustomPage] = useState(null); // null = homepage

  // Homepage config
  const [hpConfig, setHpConfig] = useState(null);
  const [hpSectionsOrder, setHpSectionsOrder] = useState([]);

  // Custom page state
  const [pageTitle, setPageTitle] = useState('');
  const [pageSlug, setPageSlug] = useState('');
  const [pageStatus, setPageStatus] = useState('draft');
  const [pageMeta, setPageMeta] = useState('');
  const [pageShowInNav, setPageShowInNav] = useState(false);
  const [blocks, setBlocks] = useState([]);

  // UI state
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null); // {id, type, isHP, section}
  const [sheetOpen, setSheetOpen] = useState(false);
  const [showAddBlock, setShowAddBlock] = useState(false);
  const [showNewPage, setShowNewPage] = useState(false);
  const [activeHPSection, setActiveHPSection] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    const [hpRes, pagesRes] = await Promise.all([
      axios.get(`${API}/page-config`).catch(() => ({ data: {} })),
      axios.get(`${API}/admin/pages`, { withCredentials: true }).catch(() => ({ data: { pages: [] } })),
    ]);
    const cfg = withDefaults(hpRes.data);
    setHpConfig(cfg);
    setHpSectionsOrder(cfg.sections_order);
    setPages(pagesRes.data.pages || []);
  };

  const selectPage = async (pageId) => {
    setSelectedPageId(pageId);
    setSheetOpen(false);
    setEditingBlock(null);
    if (pageId === 'homepage') {
      setCustomPage(null);
    } else {
      const r = await axios.get(`${API}/admin/pages/${pageId}`, { withCredentials: true });
      const p = r.data;
      setCustomPage(p);
      setPageTitle(p.title);
      setPageSlug(p.slug);
      setPageStatus(p.status);
      setPageMeta(p.meta_description || '');
      setPageShowInNav(!!p.show_in_nav);
      setBlocks((p.blocks || []).map(b => ({ ...b, _id: b._id || uid() })));
    }
  };

  // ── Save ──────────────────────────────────────────────────────
  const save = async () => {
    setSaving(true);
    try {
      if (selectedPageId === 'homepage') {
        await axios.put(`${API}/admin/page-config`, { ...hpConfig, sections_order: hpSectionsOrder }, { withCredentials: true });
      } else {
        await axios.put(`${API}/admin/pages/${selectedPageId}`, {
          title: pageTitle, slug: pageSlug, status: pageStatus, meta_description: pageMeta,
          show_in_nav: pageShowInNav,
          blocks: blocks.map(({ _id, ...rest }) => rest),
        }, { withCredentials: true });
        await loadData();
      }
      setSaved(true);
      toast.success('Page saved successfully!');
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      toast.error('Failed to save page.');
    } finally { setSaving(false); }
  };

  // ── Block ops ────────────────────────────────────────────────
  const addBlock = (type) => {
    const newBlock = { _id: uid(), type, data: { ...BLOCK_DEFAULTS[type] } };
    setBlocks(prev => [...prev, newBlock]);
    setEditingBlock(newBlock);
    setSheetOpen(true);
  };

  const updateBlock = (id, data) => {
    setBlocks(prev => prev.map(b => b._id === id ? { ...b, data } : b));
    if (editingBlock?._id === id) setEditingBlock(prev => ({ ...prev, data }));
  };

  const deleteBlock = (id) => {
    setBlocks(prev => prev.filter(b => b._id !== id));
    if (editingBlock?._id === id) { setSheetOpen(false); setEditingBlock(null); }
  };

  const duplicateBlock = (id) => {
    const block = blocks.find(b => b._id === id);
    if (!block) return;
    const dup = { ...block, _id: uid() };
    const idx = blocks.findIndex(b => b._id === id);
    const next = [...blocks];
    next.splice(idx + 1, 0, dup);
    setBlocks(next);
  };

  // ── HP section ops ───────────────────────────────────────────
  const toggleHPSection = (key) => {
    setHpConfig(prev => {
      const section = prev[key] || {};
      return { ...prev, [key]: { ...section, enabled: !section.enabled } };
    });
  };

  const openHPEditor = (key) => {
    setActiveHPSection(key);
    setEditingBlock({ isHP: true, section: key });
    setSheetOpen(true);
  };

  const updateHPSection = (key, cfg) => {
    setHpConfig(prev => ({ ...prev, [key]: cfg }));
    if (editingBlock?.section === key) setEditingBlock(prev => ({ ...prev }));
  };

  // ── Create page ─────────────────────────────────────────────
  const createPage = async (data) => {
    const r = await axios.post(`${API}/admin/pages`, data, { withCredentials: true });
    await loadData();
    selectPage(r.data.id);
    toast.success('Page created!');
  };

  // ── Delete page ─────────────────────────────────────────────
  const deletePage = async (id) => {
    if (!window.confirm('Delete this page permanently?')) return;
    await axios.delete(`${API}/admin/pages/${id}`, { withCredentials: true });
    setSelectedPageId('homepage');
    setCustomPage(null);
    await loadData();
    toast.success('Page deleted.');
  };

  const isHomepage = selectedPageId === 'homepage';

  // ── Render ───────────────────────────────────────────────────
  return (
    <TooltipProvider>
    <AdminLayout fullWidth>
      <div className="flex h-[calc(100vh-4rem)] -mx-6 -mt-6 overflow-hidden">

        {/* ─ Left Panel: Page List ─ */}
        <div className="w-64 flex-shrink-0 bg-sand border-r border-ink/10 flex flex-col">
          <div className="p-4 border-b border-ink/10">
            <p className="text-ink/55 text-xs uppercase tracking-widest mb-3">Pages</p>
            <Button onClick={() => setShowNewPage(true)} size="sm"
              data-testid="new-page-btn"
              className="w-full bg-honey/10 text-honey-700 border border-honey/20 hover:bg-honey/20 font-bold text-xs">
              <Plus className="w-3 h-3 mr-2" /> New Page
            </Button>
          </div>
          <ScrollArea className="flex-1">
            <div className="p-2 space-y-1">
              {/* Homepage */}
              <button onClick={() => selectPage('homepage')}
                data-testid="page-select-homepage"
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm transition-all text-left group ${selectedPageId === 'homepage' ? 'bg-honey/10 text-honey-700 border border-honey/20' : 'text-ink/70 hover:text-ink hover:bg-ink/[0.04]'}`}>
                <Layout className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="flex-1 font-medium truncate">Homepage</span>
                <Badge className="bg-honey/10 text-honey-600 border-honey/20 text-[10px] px-1.5 py-0">Home</Badge>
              </button>
              <Separator className="my-2 bg-ink/[0.04]" />
              {/* Custom pages */}
              {pages.map(page => (
                <button key={page.id} onClick={() => selectPage(page.id)}
                  data-testid={`page-select-${page.id}`}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm transition-all text-left ${selectedPageId === page.id ? 'bg-honey/10 text-honey-700 border border-honey/20' : 'text-ink/70 hover:text-ink hover:bg-ink/[0.04]'}`}>
                  <FileText className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="flex-1 font-medium truncate">{page.title}</span>
                  <Badge className={`text-[10px] px-1.5 py-0 border ${page.status === 'published' ? 'bg-green-500/10 text-green-600 border-green-500/20' : 'bg-ink/[0.04] text-ink/45 border-ink/10'}`}>
                    {page.status === 'published' ? 'Live' : 'Draft'}
                  </Badge>
                </button>
              ))}
              {pages.length === 0 && (
                <p className="text-ink/30 text-xs px-3 py-2 text-center">No custom pages yet</p>
              )}
            </div>
          </ScrollArea>
        </div>

        {/* ─ Center: Canvas ─ */}
        <div className="flex-1 flex flex-col min-w-0 bg-[#FFFDF8]">
          {/* Top bar */}
          <div className="flex items-center justify-between px-6 py-3 border-b border-ink/10 bg-sand gap-4">
            <div className="flex items-center gap-3 min-w-0">
              {isHomepage ? (
                <h1 className="text-ink font-black text-lg truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>Homepage</h1>
              ) : (
                <div className="flex items-center gap-2 min-w-0">
                  <Input value={pageTitle} onChange={e => setPageTitle(e.target.value)}
                    className="bg-ink/[0.04] border-ink/10 text-ink font-bold h-8 text-sm focus:border-honey w-48" />
                  <div className="flex items-center gap-1 text-ink/45 text-sm">
                    <span>/pages/</span>
                    <Input value={pageSlug} onChange={e => setPageSlug(slugify(e.target.value))}
                      className="bg-ink/[0.04] border-ink/10 text-ink/75 font-mono h-8 text-xs focus:border-honey w-48" />
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {!isHomepage && (
                <>
                  <div className="flex items-center gap-2">
                    <Switch checked={pageStatus === 'published'} onCheckedChange={v => setPageStatus(v ? 'published' : 'draft')} />
                    <Badge className={pageStatus === 'published' ? 'bg-green-500/10 text-green-600 border-green-500/20' : 'bg-ink/[0.04] text-ink/55 border-ink/10'}>
                      {pageStatus === 'published' ? 'Published' : 'Draft'}
                    </Badge>
                  </div>
                  <Tooltip><TooltipTrigger asChild>
                    <Button variant="ghost" size="sm" onClick={() => window.open(`/pages/${pageSlug}`, '_blank')}
                      className="text-ink/55 hover:text-ink h-8 px-2">
                      <ExternalLink className="w-3.5 h-3.5" />
                    </Button>
                  </TooltipTrigger><TooltipContent className="bg-ink/10 text-ink border-ink/10">Preview</TooltipContent></Tooltip>
                  <Tooltip><TooltipTrigger asChild>
                    <Button variant="ghost" size="sm" onClick={() => deletePage(selectedPageId)}
                      className="text-red-600/50 hover:text-red-600 hover:bg-red-500/10 h-8 px-2">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </TooltipTrigger><TooltipContent className="bg-ink/10 text-ink border-ink/10">Delete page</TooltipContent></Tooltip>
                </>
              )}
              <Button onClick={save} disabled={saving}
                data-testid="save-page-btn"
                className={`h-8 px-4 font-bold text-sm transition-all ${saved ? 'bg-green-500 text-ink' : 'bg-honey text-black hover:bg-honey-600'}`}>
                {saving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : saved ? <><Check className="w-3.5 h-3.5 mr-1" />Saved</> : <><Save className="w-3.5 h-3.5 mr-1" />Save</>}
              </Button>
            </div>
          </div>

          {/* Canvas body */}
          <ScrollArea className="flex-1">
            <div className="p-6 max-w-3xl mx-auto space-y-3">

              {/* ── Homepage canvas ── */}
              {isHomepage && hpConfig && (
                <>
                  {/* Announcement bar (not in drag order) */}
                  <div className={`rounded-xl border transition-all cursor-pointer group ${hpConfig.announcement_bar?.enabled ? 'border-honey/30 bg-honey/5' : 'border-ink/10 bg-ink/[0.02] opacity-50'}`}
                    onClick={() => openHPEditor('announcement_bar')}>
                    <div className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Megaphone className="w-4 h-4 text-honey-600" />
                        <div>
                          <p className="text-ink text-sm font-semibold">Announcement Bar</p>
                          <p className="text-ink/45 text-xs">{hpConfig.announcement_bar?.text?.slice(0, 50) || 'No message set'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                        <Switch checked={!!hpConfig.announcement_bar?.enabled} onCheckedChange={() => toggleHPSection('announcement_bar')} />
                        <Settings className="w-4 h-4 text-ink/30 group-hover:text-ink/70 transition-colors" />
                      </div>
                    </div>
                  </div>

                  <Separator className="bg-ink/[0.04] my-1" />
                  <p className="text-ink/30 text-xs px-1">Page Sections (drag to reorder)</p>

                  <Reorder.Group values={hpSectionsOrder} onReorder={setHpSectionsOrder} className="space-y-2">
                    {hpSectionsOrder.map(sectionKey => {
                      const meta = HP_SECTIONS[sectionKey];
                      if (!meta) return null;
                      const Icon = meta.icon;
                      const cfg = hpConfig[sectionKey] || {};
                      const enabled = cfg.enabled !== false;
                      return (
                        <Reorder.Item key={sectionKey} value={sectionKey}
                          className={`rounded-xl border transition-all cursor-pointer group ${enabled ? 'border-ink/15 bg-ink/[0.04] hover:border-honey/30' : 'border-ink/10 bg-ink/[0.02] opacity-60'}`}>
                          <div className="flex items-center justify-between px-4 py-3" onClick={() => openHPEditor(sectionKey)}>
                            <div className="flex items-center gap-3">
                              <GripVertical className="w-4 h-4 text-ink/30 cursor-grab flex-shrink-0" />
                              <Icon className="w-4 h-4 text-honey-700" />
                              <div>
                                <p className="text-ink text-sm font-semibold">{meta.label}</p>
                                <p className="text-ink/45 text-xs">{meta.desc}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                              <Switch checked={enabled} onCheckedChange={() => toggleHPSection(sectionKey)} />
                              <Settings className="w-4 h-4 text-ink/30 group-hover:text-ink/70 transition-colors" />
                            </div>
                          </div>
                        </Reorder.Item>
                      );
                    })}
                  </Reorder.Group>
                </>
              )}

              {/* ── Custom page canvas ── */}
              {!isHomepage && (
                <>
                  {blocks.length === 0 && (
                    <div className="border-2 border-dashed border-ink/10 rounded-2xl py-16 text-center">
                      <Layers className="w-10 h-10 text-ink/15 mx-auto mb-3" />
                      <p className="text-ink/45 text-sm">No blocks yet</p>
                      <p className="text-ink/30 text-xs mt-1">Click "Add Block" to start building</p>
                    </div>
                  )}
                  <Reorder.Group values={blocks.map(b => b._id)} onReorder={(ids) => setBlocks(ids.map(id => blocks.find(b => b._id === id)).filter(Boolean))} className="space-y-2">
                    {blocks.map((block) => {
                      const bt = BLOCK_TYPES.find(t => t.type === block.type);
                      const Icon = bt?.icon || Type;
                      const isEditing = editingBlock?._id === block._id;
                      return (
                        <Reorder.Item key={block._id} value={block._id}
                          className={`rounded-xl border transition-all group ${isEditing ? 'border-honey/50 bg-honey/5' : 'border-ink/10 bg-ink/[0.04] hover:border-ink/15'}`}>
                          <div className="flex items-center gap-3 px-4 py-3">
                            <GripVertical className="w-4 h-4 text-ink/30 cursor-grab flex-shrink-0" />
                            <Icon className="w-4 h-4 text-ink/55 flex-shrink-0" />
                            <div className="flex-1 min-w-0 cursor-pointer" onClick={() => { setEditingBlock(block); setSheetOpen(true); }}>
                              <p className="text-ink text-sm font-semibold capitalize">{bt?.label || block.type}</p>
                              <p className="text-ink/45 text-xs truncate">{blockSummary(block)}</p>
                            </div>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Tooltip><TooltipTrigger asChild>
                                <Button variant="ghost" size="sm" onClick={() => duplicateBlock(block._id)}
                                  className="h-7 w-7 p-0 text-ink/45 hover:text-ink hover:bg-ink/[0.07]">
                                  <Copy className="w-3.5 h-3.5" />
                                </Button>
                              </TooltipTrigger><TooltipContent className="bg-ink/10 text-ink border-ink/10 text-xs">Duplicate</TooltipContent></Tooltip>
                              <Tooltip><TooltipTrigger asChild>
                                <Button variant="ghost" size="sm" onClick={() => deleteBlock(block._id)}
                                  data-testid={`delete-block-${block._id}`}
                                  className="h-7 w-7 p-0 text-red-600/50 hover:text-red-600 hover:bg-red-500/10">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </TooltipTrigger><TooltipContent className="bg-ink/10 text-ink border-ink/10 text-xs">Delete</TooltipContent></Tooltip>
                            </div>
                          </div>
                        </Reorder.Item>
                      );
                    })}
                  </Reorder.Group>

                  {/* Add block button */}
                  <button onClick={() => setShowAddBlock(true)}
                    data-testid="add-block-btn"
                    className="w-full border-2 border-dashed border-ink/10 rounded-xl py-4 flex items-center justify-center gap-2 text-ink/45 hover:text-honey-700 hover:border-honey/30 transition-all group">
                    <Plus className="w-4 h-4" />
                    <span className="text-sm font-medium">Add Block</span>
                  </button>

                  {/* Page settings */}
                  <Accordion type="single" collapsible className="mt-4">
                    <AccordionItem value="settings" className="border border-ink/10 rounded-xl overflow-hidden">
                      <AccordionTrigger className="px-4 py-3 text-ink/60 hover:text-ink text-sm hover:no-underline">
                        <div className="flex items-center gap-2">
                          <Settings className="w-4 h-4" /> Page Settings & SEO
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4 space-y-4">
                        <Field label="Page Title" value={pageTitle} onChange={setPageTitle} placeholder="About Us" />
                        <div className="space-y-1.5">
                          <Label className="text-ink/70 text-xs uppercase tracking-wider">URL Slug</Label>
                          <div className="flex items-center gap-2">
                            <span className="text-ink/45 text-xs">/pages/</span>
                            <Input value={pageSlug} onChange={e => setPageSlug(slugify(e.target.value))}
                              className="bg-ink/[0.04] border-ink/10 text-ink font-mono text-xs focus:border-honey" />
                          </div>
                        </div>
                        <FieldArea label="Meta Description (SEO)" value={pageMeta} onChange={setPageMeta} placeholder="Brief description of this page for search engines..." rows={2} hint="140–160 characters recommended" />
                        <div className="flex items-center justify-between py-2 px-3 rounded-xl bg-ink/[0.04] border border-ink/10">
                          <div>
                            <p className="text-ink/75 text-sm font-medium">Show in Navbar</p>
                            <p className="text-ink/45 text-xs">Display a link in the storefront navigation</p>
                          </div>
                          <Switch checked={pageShowInNav} onCheckedChange={setPageShowInNav} />
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </>
              )}
            </div>
          </ScrollArea>
        </div>

        {/* ─ Right: Docked Editor Panel ─ */}
        <AnimatePresence>
          {sheetOpen && editingBlock && (
            <motion.aside
              key="editor-panel"
              initial={{ x: 40, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 40, opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="w-96 flex-shrink-0 bg-white border-l border-ink/10 flex flex-col"
              data-testid="block-editor-panel"
            >
              <div className="px-5 py-4 border-b border-ink/10 flex-shrink-0 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <PanelRight className="w-4 h-4 text-honey-700 flex-shrink-0" />
                  <h2 className="text-ink font-black text-base truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {editingBlock?.isHP
                      ? HP_SECTIONS[editingBlock.section]?.label || 'Edit Section'
                      : BLOCK_TYPES.find(t => t.type === editingBlock?.type)?.label || 'Edit Block'
                    }
                  </h2>
                </div>
                <Button variant="ghost" size="sm"
                  onClick={() => { setSheetOpen(false); setEditingBlock(null); setActiveHPSection(null); }}
                  data-testid="close-editor-panel"
                  className="h-7 w-7 p-0 text-ink/55 hover:text-ink hover:bg-ink/[0.07] flex-shrink-0">
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <ScrollArea className="flex-1 px-5 py-4">
                {/* Homepage section editor */}
                {editingBlock?.isHP && editingBlock.section && hpConfig && (() => {
                  const EditorComp = HP_EDITORS[editingBlock.section];
                  if (!EditorComp) return <p className="text-ink/45 text-sm">No editor for this section.</p>;
                  return (
                    <EditorComp
                      cfg={hpConfig[editingBlock.section] || {}}
                      onChange={cfg => updateHPSection(editingBlock.section, cfg)}
                    />
                  );
                })()}

                {/* Custom block editor */}
                {!editingBlock?.isHP && editingBlock && (() => {
                  const block = blocks.find(b => b._id === editingBlock._id);
                  if (!block) return null;
                  const EditorComp = BLOCK_EDITORS[block.type];
                  if (!EditorComp) return <p className="text-ink/45 text-sm">No editor for this block type.</p>;
                  return <EditorComp data={block.data || {}} onChange={data => updateBlock(block._id, data)} />;
                })()}
              </ScrollArea>
            </motion.aside>
          )}
        </AnimatePresence>
      </div>

      {/* Dialogs */}
      <AddBlockDialog open={showAddBlock} onClose={() => setShowAddBlock(false)} onAdd={addBlock} />
      <NewPageDialog open={showNewPage} onClose={() => setShowNewPage(false)} onCreate={createPage} />
    </AdminLayout>
    </TooltipProvider>
  );
}
